const isNonEmptyString = (v) => typeof v === "string" && v.trim().length > 0

const stripCreateRequestPrefix = (text) =>
  text.replace(/^An error occurred while creating the request:\s*/i, "").trim()

/** Unescape fragments as they appear in Python/DRF ErrorDetail repr inside JSON strings. */
const unescapePythonErrorDetailFragment = (s) => {
  if (!isNonEmptyString(s)) return ""
  return s
    .replace(/\\\\/g, "\u0000BACKSLASH\u0000")
    .replace(/\\'/g, "'")
    .replace(/\\"/g, '"')
    .replace(/\u0000BACKSLASH\u0000/g, "\\")
}

/**
 * Pulls each `ErrorDetail(string='...', code=...)` payload from a noisy backend string.
 * Handles escaped quotes inside the string via backslash rules.
 */
const extractErrorDetailStrings = (text) => {
  if (!isNonEmptyString(text)) return []
  const marker = "ErrorDetail(string='"
  const results = []
  let i = 0
  while (i < text.length) {
    const start = text.indexOf(marker, i)
    if (start === -1) break
    let pos = start + marker.length
    let buf = ""
    let closed = false
    while (pos < text.length) {
      const c = text[pos]
      if (c === "\\" && pos + 1 < text.length) {
        buf += text[pos] + text[pos + 1]
        pos += 2
        continue
      }
      if (c === "'" && /^',\s*code=/.test(text.slice(pos))) {
        results.push(unescapePythonErrorDetailFragment(buf))
        closed = true
        i = pos + 1
        break
      }
      buf += c
      pos += 1
    }
    if (!closed) break
  }
  return results
}

/** Best-effort cleanup when the API returns a Python/DRF repr instead of plain text. */
const shortenMessyBackendError = (text) => {
  if (!isNonEmptyString(text)) return ""
  let s = stripCreateRequestPrefix(text)
  const failedIdx = s.indexOf("Failed to upload")
  if (failedIdx !== -1) {
    s = s.slice(failedIdx)
  }
  const edIdx = s.indexOf("ErrorDetail")
  if (edIdx !== -1) {
    s = s.slice(0, edIdx)
  }
  return s.replace(/\s+/g, " ").trim()
}

const pickErrorDetailForLang = (details, isAr) => {
  if (!details.length) return ""
  if (isAr && details.length > 1) {
    return details[1].trim() || details[0].trim()
  }
  return details[0].trim()
}

/**
 * `{ success: false, details: "{'message': '…', 'messageArabic': '…'}" }`
 */
const messageFromDetailsField = (details, isAr) => {
  if (details == null) return ""

  let payload = details
  if (typeof details === "string") {
    const s = details.trim()
    if (!s) return ""
    try {
      const jsonish = s.replace(/'/g, '"').replace(/\bTrue\b/g, "true").replace(/\bFalse\b/g, "false")
      payload = JSON.parse(jsonish)
    } catch {
      const pick = (key) => {
        const re = new RegExp(
          `['"]${key}['"]\\s*:\\s*['"]((?:\\\\.|[^'"])*)['"]`,
          "i",
        )
        const m = s.match(re)
        return m?.[1]?.replace(/\\'/g, "'").replace(/\\"/g, '"').trim() || ""
      }
      const en = pick("message") || pick("error")
      const ar = pick("messageArabic") || pick("errorArabic")
      if (isAr) return ar || en
      return en || ar
    }
  }

  if (!payload || typeof payload !== "object") return ""

  const primary = isAr
    ? payload.messageArabic ?? payload.errorArabic
    : payload.message ?? payload.error
  const secondary = isAr
    ? payload.message ?? payload.error
    : payload.messageArabic ?? payload.errorArabic
  if (isNonEmptyString(primary)) return String(primary).trim()
  if (isNonEmptyString(secondary)) return String(secondary).trim()
  return ""
}

const messageFromErrorBlobs = (primaryBlob, secondaryBlob, isAr) => {
  for (const blob of [primaryBlob, secondaryBlob]) {
    if (!isNonEmptyString(blob)) continue
    const str = String(blob)
    const details = extractErrorDetailStrings(str)
    const picked = pickErrorDetailForLang(details, isAr)
    if (isNonEmptyString(picked)) return picked
    const cleaned = shortenMessyBackendError(str)
    if (isNonEmptyString(cleaned)) return cleaned
  }
  return ""
}

/**
 * Resolves a user-facing message for POST/PATCH /api/store/ style responses.
 * Handles: { message, messageArabic }, { error, errorArabic }, and noisy backend dumps.
 * Prefers parsed API text (e.g. ErrorDetail) over generic copy when available.
 *
 * @param {Record<string, unknown>|null|undefined} data - error.response.data
 * @param {string} lang - 'en' | 'ar' (or anything else treated like 'en')
 * @param {{ generic?: string }} copy - translated fallback when nothing can be parsed
 */
export function getStoreApiErrorMessage(data, lang, copy = {}) {
  const isAr = lang === "ar"
  const { generic } = copy

  if (!data || typeof data !== "object") {
    return generic || (isAr ? "حدث خطأ. يُرجى المحاولة مرة أخرى." : "Something went wrong. Please try again.")
  }

  const primaryMsg = isAr ? data.messageArabic : data.message
  const secondaryMsg = isAr ? data.message : data.messageArabic

  if (isNonEmptyString(primaryMsg)) {
    return String(primaryMsg).trim()
  }
  if (isNonEmptyString(secondaryMsg)) {
    return String(secondaryMsg).trim()
  }

  const fromDetails = messageFromDetailsField(data.details, isAr)
  if (isNonEmptyString(fromDetails)) {
    return fromDetails
  }

  for (const field of [
    isAr ? data.errorArabic : data.error,
    isAr ? data.error : data.errorArabic,
  ]) {
    const fromErrorDict = messageFromDetailsField(field, isAr)
    if (isNonEmptyString(fromErrorDict)) {
      return String(fromErrorDict).trim()
    }
  }

  const primaryErr = isAr ? data.errorArabic : data.error
  const secondaryErr = isAr ? data.error : data.errorArabic

  const fromBlobs = messageFromErrorBlobs(primaryErr, secondaryErr, isAr)
  if (isNonEmptyString(fromBlobs)) {
    return fromBlobs
  }

  const detail = isAr ? data.detailArabic ?? data.detaiArabic : data.detail
  if (isNonEmptyString(detail)) {
    return String(detail).trim()
  }

  return (
    generic ||
    (isAr ? "تعذّر إتمام الطلب. يُرجى المحاولة مرة أخرى." : "Could not complete the request. Please try again.")
  )
}
